import type { FimSubjectRecord } from 'features/fim/types'
import type { RolodexSignedURLResult } from 'types'

import {
  type FimPhotoEntry,
  PHOTO_FILE_PATH,
  PHOTO_THUMBNAIL_PATH,
  type SubjectPhoto,
} from './types'

/**
 * Photo entry ids fusion has already landed on the subject: the locators of
 * `type=photo` references on the subject payload's aliases.
 */
function naturalizedPhotoIds(subject: FimSubjectRecord): ReadonlySet<string> {
  const ids = new Set<string>()
  for (const alias of subject.data.aliases) {
    for (const reference of alias.references) {
      if (reference.type === 'photo' && reference.locator) {
        ids.add(reference.locator)
      }
    }
  }
  return ids
}

const aliasNorms = (subject: FimSubjectRecord): ReadonlySet<string> =>
  new Set(subject.data.aliases.map((alias) => alias.norm))

/**
 * The subject's photos among the site's attached entries: those fusion has
 * landed on the subject payload plus those elected to one of its alias norms.
 * A detached photo loses its `attached` status, so it leaves this list before
 * fusion drops its reference. `subject_uid_at_election` is never joined on.
 */
export function subjectPhotoEntries(
  subject: FimSubjectRecord,
  sitePhotos: FimPhotoEntry[]
): FimPhotoEntry[] {
  const naturalizedIds = naturalizedPhotoIds(subject)
  const norms = aliasNorms(subject)
  return sitePhotos
    .filter(
      (entry) =>
        naturalizedIds.has(entry.id) ||
        entry.data.elections.some((election) =>
          norms.has(election.subject_identity_norm)
        )
    )
    .sort((a, b) => {
      const byCapture = a.data.captured_at.localeCompare(b.data.captured_at)
      return byCapture !== 0 ? byCapture : a.id.localeCompare(b.id)
    })
}

const heroStamp = (entry: FimPhotoEntry, norms: ReadonlySet<string>): string =>
  entry.data.elections.reduce((latest, election) => {
    const stamp = election.hero_at ?? ''
    return norms.has(election.subject_identity_norm) && stamp > latest
      ? stamp
      : latest
  }, '')

const uploadedAt = (entry: FimPhotoEntry): string =>
  entry.data.uploaded_at ?? entry.data.captured_at

export function subjectHeroEntry(
  subject: FimSubjectRecord,
  entries: FimPhotoEntry[]
): FimPhotoEntry | undefined {
  const norms = aliasNorms(subject)
  return entries.reduce<FimPhotoEntry | undefined>((hero, entry) => {
    if (!hero) return entry
    const byStamp = heroStamp(entry, norms).localeCompare(
      heroStamp(hero, norms)
    )
    if (byStamp !== 0) return byStamp > 0 ? entry : hero
    const byUpload = uploadedAt(entry).localeCompare(uploadedAt(hero))
    if (byUpload !== 0) return byUpload < 0 ? entry : hero
    return entry.id < hero.id ? entry : hero
  }, undefined)
}

const signedUrlKey = (entryId: string, path: string): string =>
  `${entryId}\n${path}`

/** `pending` until fusion lands the reference on the subject payload. */
export function toSubjectPhotos(
  subject: FimSubjectRecord,
  entries: FimPhotoEntry[],
  signedUrls: ReadonlyArray<RolodexSignedURLResult>
): SubjectPhoto[] {
  const naturalizedIds = naturalizedPhotoIds(subject)
  const urlByKey = new Map(
    signedUrls.map((result) => [
      signedUrlKey(result.entry_id, result.path),
      result.signed_url,
    ])
  )
  return entries.map((entry) => ({
    entry,
    pending: !naturalizedIds.has(entry.id),
    thumbnailUrl: urlByKey.get(signedUrlKey(entry.id, PHOTO_THUMBNAIL_PATH)),
    imageUrl: urlByKey.get(signedUrlKey(entry.id, PHOTO_FILE_PATH)),
  }))
}
